import { Images } from "lucide-react";
import type { YesNoNode as YesNoNodeType } from "@/lib/pharmacyFirst/types";
import { ClinicalLinkChips } from "./SingleSelectNode";
import { ImageCarousel } from "./ImageCarousel";

interface YesNoNodeProps {
  node: YesNoNodeType;
  isDarkMode?: boolean;
  onAnswer: (value: "yes" | "no") => void;
  value?: "yes" | "no" | null;
  stepNumber?: number;
}

function HintContent({
  hint,
  isDarkMode,
}: {
  hint: string;
  isDarkMode: boolean;
}) {
  // Split by periods followed by space, but preserve periods that are part of abbreviations
  const sentences = hint.split(/(?<=[.;])\s+/).filter((s) => s.trim());

  // If there's more than one sentence, render as list
  if (sentences.length > 1) {
    return (
      <ul
        className={`text-xs mt-2 space-y-1 list-disc list-inside ${
          isDarkMode ? "text-gray-400" : "text-gray-600"
        }`}
      >
        {sentences.map((sentence, index) => (
          <li key={index}>{sentence}</li>
        ))}
      </ul>
    );
  }

  // Single sentence, render as paragraph
  return (
    <p
      className={`text-xs mt-1 ${
        isDarkMode ? "text-gray-400" : "text-gray-600"
      }`}
    >
      {hint}
    </p>
  );
}

function ImageHoverPopover({
  popover,
  isDarkMode,
}: {
  popover?: YesNoNodeType["imagePopover"];
  isDarkMode: boolean;
}) {
  if (!popover || popover.items.length === 0) return null;

  const carouselImages = popover.items.map((item) => ({
    url: item.imageUrl,
    caption: item.caption,
  }));

  return (
    <ImageCarousel
      title={popover.label}
      images={carouselImages}
      isDarkMode={isDarkMode}
    />
  );
}

export function YesNoNode({
  node,
  isDarkMode = false,
  onAnswer,
  value = null,
  stepNumber,
}: YesNoNodeProps) {
  return (
    <div
      className={`border rounded-2xl p-4 ${
        isDarkMode ? "border-gray-700 bg-gray-800" : "border-gray-200 bg-white"
      }`}
    >
      {stepNumber !== undefined && (
        <div className="flex items-center gap-3 mb-4">
          <div
            className={`flex items-center justify-center w-6 h-6 rounded-full text-sm font-semibold ${
              isDarkMode ? "bg-white text-black" : "bg-black text-white"
            }`}
          >
            {stepNumber}
          </div>
          <div className="flex-1">
            <h3
              className={`text-sm font-semibold ${
                isDarkMode ? "text-white" : "text-gray-900"
              }`}
            >
              {node.text}
            </h3>
            {node.hint && (
              <HintContent hint={node.hint} isDarkMode={isDarkMode} />
            )}
            <ClinicalLinkChips
              links={node.clinicalLinks}
              isDarkMode={isDarkMode}
            />
            <ImageHoverPopover
              popover={node.imagePopover}
              isDarkMode={isDarkMode}
            />
          </div>
        </div>
      )}

      {stepNumber === undefined && (
        <div className="mb-4">
          <h3
            className={`text-sm font-semibold ${
              isDarkMode ? "text-white" : "text-gray-900"
            }`}
          >
            {node.text}
          </h3>
          {node.hint && (
            <HintContent hint={node.hint} isDarkMode={isDarkMode} />
          )}
          <ClinicalLinkChips
            links={node.clinicalLinks}
            isDarkMode={isDarkMode}
          />
          <ImageHoverPopover
            popover={node.imagePopover}
            isDarkMode={isDarkMode}
          />
        </div>
      )}

      {node.showSepsisWarning && (
        <div
          className={`mb-4 border rounded-lg p-4 ${
            isDarkMode
              ? "border-red-700 bg-red-950"
              : "border-red-300 bg-red-50"
          }`}
        >
          <h4
            className={`text-sm font-semibold mb-3 ${
              isDarkMode ? "text-red-200" : "text-red-900"
            }`}
          >
            How to spot sepsis in adults
          </h4>
          <ol
            className={`text-xs space-y-1.5 list-decimal list-inside ${
              isDarkMode ? "text-red-100" : "text-red-800"
            }`}
          >
            <li>Slurred speech or confusion</li>
            <li>Extreme shivering or muscle pain</li>
            <li>Passing no urine (in a day)</li>
            <li>Severe breathlessness</li>
            <li>It feels like you're going to die</li>
            <li>Skin mottled or discoloured</li>
          </ol>
        </div>
      )}

      <div className="grid grid-cols-2 gap-2" key={node.id}>
        {(["yes", "no"] as const).map((opt) => {
          const selected = value === opt;
          const base =
            "px-4 py-2.5 rounded-lg border-2 text-sm font-semibold transition select-none focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1";

          let selectedCls: string;
          if (opt === "yes") {
            // Red for Yes
            selectedCls = isDarkMode
              ? "border-red-600 bg-red-950 text-red-100 shadow-sm"
              : "border-red-300 bg-red-50 text-red-900 shadow-sm";
          } else {
            // Green for No
            selectedCls = isDarkMode
              ? "border-green-600 bg-green-950 text-green-100 shadow-sm"
              : "border-green-300 bg-green-50 text-green-900 shadow-sm";
          }

          const unselectedCls = isDarkMode
            ? "border-gray-600 bg-gray-700 text-gray-200 hover:bg-gray-600"
            : "border-gray-300 bg-white text-gray-800 hover:bg-gray-50";
          return (
            <button
              key={`${node.id}-${opt}`}
              type="button"
              aria-pressed={selected}
              onClick={(e) => {
                onAnswer(opt);
                (e.currentTarget as HTMLButtonElement).blur();
              }}
              className={`${base} ${selected ? selectedCls : unselectedCls}`}
            >
              {opt === "yes" ? "Yes" : "No"}
            </button>
          );
        })}
      </div>
    </div>
  );
}
